import React from 'react';
import { useWatch } from 'antd/es/form/Form';
import { LoadingOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { preventNonNumericValue } from 'utils/helpers';
import { ValidationErrors } from 'constants/validations';
import { Form, FormInstance, InputNumber, Row, Spin } from 'antd';
import { EditIcon, PlusIcon } from 'components/common/icons';
import { Button, Col, Radio, Select, Typography } from 'components';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { CustomAddressAddEdit } from './customAddressAddEditModal';
import { useDelivery } from './useDelivery';

const {Text} = Typography;

const DELIVERY_TERMS = [
  {
    label: 'EXW',
    value: 'EXW',
  },
  {
    label: 'DAP',
    value: 'DAP',
  },
  {
    label: 'DDP',
    value: 'DDP',
  },
];

const style: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

interface IDelivery {
  form: FormInstance;
  clientShippingAddress: Record<string, any>[];
  deliveryDetails: Record<string, any>;
  onFinish: () => void;
  onAddModalStateChange: () => void;
  isFinalStatus?: boolean;
  isLoading?: boolean;
}

export const Delivery = React.memo((props: IDelivery) => {
  const {form, clientShippingAddress, deliveryDetails, onFinish, onAddModalStateChange, isFinalStatus, isLoading} =
    props;
  const {hasAccess} = useAccessControl();
  const {customAddressState, dataToEdit, onCustomAddressStateChange} = useDelivery();
  const deliveryTerm = useWatch(['delivery', 'deliveryTerms'], form);

  const isEXW = deliveryTerm === 'EXW';
  const isDDP = deliveryTerm === 'DDP';
  const isDAP = deliveryTerm === 'DAP';
  const showFullForm = isDDP || isDAP;
  return (
    <>
      <Row style={{padding: '20px'}} gutter={[0, 20]}>
        <Col span={24}>
          <Text style={{color: '#05796B'}} className={'font-weight-800 font-size-16'}>
            Delivery
          </Text>
        </Col>
        <Col span={24}>
          <Row gutter={[10, 16]} align={'middle'}>
            <Col span={7}>
              <Form.Item name={['delivery', 'deliveryTerms']} label={'Delivery Terms'}>
                <Select
                  disabled={isFinalStatus}
                  options={DELIVERY_TERMS}
                  size={'large'}
                  onChange={(value) => {
                    setTimeout(() => {
                      if (value === 'EXW') {
                        form.setFieldsValue({
                          delivery: {
                            deliveryTerms: value,
                            insurance: undefined,
                            shippingAddressId: undefined,
                            deliveryCost: undefined,
                            deliveryMargin: undefined,
                            additionalServices: undefined,
                            packagingCost: undefined,
                            localTaxes: undefined,
                            importFees: undefined,
                          },
                        });
                      } else if (value === 'DAP') {
                        form.setFieldsValue({
                          delivery: {
                            deliveryTerms: value,
                            localTaxes: undefined,
                            importFees: undefined,
                            additionalServices: undefined,
                            packagingCost: undefined,
                            shippingAddressId: undefined,
                            deliveryCost: undefined,
                            deliveryMargin: undefined,
                            insurance: 2,
                          },
                        });
                      } else {
                        form.setFieldsValue({
                          delivery: {
                            deliveryTerms: value,
                            localTaxes: undefined,
                            importFees: undefined,
                            additionalServices: undefined,
                            packagingCost: undefined,
                            shippingAddressId: undefined,
                            deliveryCost: undefined,
                            deliveryMargin: undefined,
                            insurance: 2,
                          },
                        });
                      }
                      onFinish();
                    }, 0);
                  }}
                />
              </Form.Item>
            </Col>
            {isEXW && (
              <>
                <Col span={7}>
                  <Form.Item
                    label="Additional Services"
                    name={['delivery', 'additionalServices']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === '') {
                            return Promise.resolve();
                          }
                          const num = parseFloat(value);
                          if (isNaN(num)) {
                            return Promise.reject(new Error('Additional Services must be a valid number.'));
                          }
                          if (num < 0) {
                            return Promise.reject(new Error('Additional Services must be a positive number.'));
                          }
                          if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                            return Promise.reject(new Error('Additional Services can have up to 2 decimal points.'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber<number>
                      disabled={isFinalStatus}
                      controls={false}
                      size={'large'}
                      onBlur={onFinish}
                      onBeforeInput={(event: any) => preventNonNumericValue(event)}
                      style={{width: '100%'}}
                      suffix={<Text type={'secondary'}>€</Text>}
                    />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    label="Packaging Cost"
                    name={['delivery', 'packagingCost']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === '') {
                            return Promise.resolve();
                          }
                          const num = parseFloat(value);
                          if (isNaN(num)) {
                            return Promise.reject(new Error('Packaging Cost must be a valid number.'));
                          }
                          if (num < 0) {
                            return Promise.reject(new Error('Packaging Cost must be a positive number.'));
                          }
                          if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                            return Promise.reject(new Error('Packaging Cost can have up to 2 decimal points.'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber<number>
                      disabled={isFinalStatus}
                      controls={false}
                      size={'large'}
                      onBlur={onFinish}
                      onBeforeInput={(event: any) => preventNonNumericValue(event)}
                      style={{width: '100%'}}
                      suffix={<Text type={'secondary'}>€</Text>}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {showFullForm && (
              <>
                <Col span={6}>
                  <Form.Item
                    label="Insurance"
                    name={['delivery', 'insurance']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === '') {
                            return Promise.resolve();
                          }
                          const num = parseFloat(value);
                          if (isNaN(num)) {
                            return Promise.reject(new Error('Insurance must be a valid number.'));
                          }
                          if (num < 0) {
                            return Promise.reject(new Error('Insurance must be a positive number.'));
                          }
                          if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                            return Promise.reject(new Error('Insurance can have up to 2 decimal points.'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber<number>
                      disabled={isFinalStatus}
                      controls={false}
                      size={'large'}
                      onBlur={onFinish}
                      onBeforeInput={(event: any) => preventNonNumericValue(event)}
                      style={{width: '100%'}}
                      suffix={<Text type={'secondary'}>%</Text>}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label={<span style={{display: 'none'}}></span>}>
                    <Text className={'font-weight-600 font-size-16 no-wrap'}>= {deliveryDetails.insuranceFee}€</Text>
                  </Form.Item>
                </Col>
                <Col flex={'1'}>
                  <Form.Item
                    label="Packaging Cost"
                    name={['delivery', 'packagingCost']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === '') {
                            return Promise.resolve();
                          }
                          const num = parseFloat(value);
                          if (isNaN(num)) {
                            return Promise.reject(new Error('Packaging Cost must be a valid number.'));
                          }
                          if (num < 0) {
                            return Promise.reject(new Error('Packaging Cost must be a positive number.'));
                          }
                          if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                            return Promise.reject(new Error('Packaging Cost can have up to 2 decimal points.'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber<number>
                      disabled={isFinalStatus}
                      controls={false}
                      size={'large'}
                      onBlur={onFinish}
                      onBeforeInput={(event: any) => preventNonNumericValue(event)}
                      style={{width: '100%'}}
                      suffix={<Text type={'secondary'}>€</Text>}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name={['delivery', 'customAddress']} initialValue={deliveryDetails.customAddress}>
                    <Radio.Group
                      onChange={async () => {
                        form.setFieldValue(['delivery', 'shippingAddressId'], null);
                        await form.validateFields([['delivery', 'shippingAddressId']]);
                        onFinish();
                      }}
                      className="scribex-erp-warehouse-radio-button"
                      style={style}
                      value={form.getFieldValue('customAddress')}
                    >
                      <Form.Item shouldUpdate noStyle>
                        {({getFieldValue}) => {
                          const current = getFieldValue(['delivery', 'customAddress']);
                          return (
                            <Row className={'warehouse-radio-button'} gutter={[0, 10]}>
                              <Col span={24}>
                                <Radio value={false}>
                                  <Text className="font-weight-500 font-size-16">Client Address</Text>
                                </Radio>
                              </Col>
                              {!current && (
                                <Col span={24}>
                                  <Form.Item
                                    label="Client Address*"
                                    name={['delivery', 'shippingAddressId']}
                                    dependencies={[['delivery', 'customAddress']]}
                                    rules={[
                                      {
                                        required: true,
                                        message: ValidationErrors.required('Client Address'),
                                      },
                                    ]}
                                  >
                                    <Select
                                      disabled={isFinalStatus}
                                      size={'large'}
                                      options={clientShippingAddress}
                                      onChange={onFinish}
                                      showAdd={hasAccess(PERMISSIONS.clients.client.write)}
                                      onAdd={() => onAddModalStateChange()}
                                    />
                                  </Form.Item>
                                </Col>
                              )}
                            </Row>
                          );
                        }}
                      </Form.Item>
                      <Form.Item shouldUpdate noStyle>
                        {({getFieldValue}) => {
                          const current = getFieldValue(['delivery', 'customAddress']);
                          const {name, country, state, city, streetAddress, zip} =
                          deliveryDetails.shippingAddress || {};
                          const customAddressLabel = [name, country?.name, state, city, streetAddress, zip]
                            .filter(Boolean)
                            .join(', ');
                          return (
                            <Row className={'warehouse-radio-button'} gutter={[0, 10]}>
                              <Col span={24}>
                                <Radio value={true}>
                                  <Row align={'middle'} justify={'space-between'}>
                                    <Text className="font-weight-500 font-size-16">Custom Address</Text>
                                    {current &&
                                      (deliveryDetails.shippingAddress && deliveryDetails.hasCustomAddress ? (
                                        <Button
                                          onClick={() => onCustomAddressStateChange(deliveryDetails.shippingAddress)}
                                          type={'text'}
                                          size={'small'}
                                          icon={<EditIcon/>}
                                        />
                                      ) : (
                                        <Row
                                          align={'middle'}
                                          className={'gap-4'}
                                          onClick={() => onCustomAddressStateChange()}
                                        >
                                          <PlusIcon color={'#05796B'}/>
                                          <Text underline style={{color: '#05796B'}}>
                                            Add Address
                                          </Text>
                                        </Row>
                                      ))}
                                  </Row>
                                </Radio>
                              </Col>
                              <Col>
                                {current && (
                                  <Col span={24}>
                                    <Form.Item
                                      name={'customShippingAddressId'}
                                      dependencies={[['delivery', 'customAddress']]}
                                      rules={[
                                        {
                                          required: !deliveryDetails.hasCustomAddress,
                                          message: ValidationErrors.required('Custom Address'),
                                        },
                                      ]}
                                    >
                                      <Spin
                                        spinning={isLoading}
                                        indicator={<LoadingOutlined style={{fontSize: 16}}/>}
                                      >
                                        {deliveryDetails.hasCustomAddress && (
                                          <Text type="secondary">{customAddressLabel}</Text>
                                        )}
                                      </Spin>
                                    </Form.Item>
                                  </Col>
                                )}
                              </Col>
                            </Row>
                          );
                        }}
                      </Form.Item>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={13}>
                  <Row align={'middle'} justify={'space-between'}>
                    <Col span={11}>
                      <Form.Item
                        label="Delivery Cost"
                        name={['delivery', 'deliveryCost']}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (value === undefined || value === null || value === '') {
                                return Promise.resolve();
                              }
                              const num = parseFloat(value);
                              if (isNaN(num)) {
                                return Promise.reject(new Error('Delivery Cost must be a valid number.'));
                              }
                              if (num < 0) {
                                return Promise.reject(new Error('Delivery Cost must be a positive number.'));
                              }
                              if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                                return Promise.reject(new Error('Delivery Cost can have up to 2 decimal points.'));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber<number>
                          disabled={isFinalStatus}
                          controls={false}
                          size={'large'}
                          onBlur={onFinish}
                          onBeforeInput={(event: any) => preventNonNumericValue(event)}
                          style={{width: '100%'}}
                          suffix={<Text type={'secondary'}>€</Text>}
                        />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Form.Item label={<span style={{display: 'none'}}></span>}>
                        <Text className={'font-weight-600 font-size-16'}>+</Text>
                      </Form.Item>
                    </Col>
                    <Col span={11}>
                      <Form.Item
                        label="Delivery Margin"
                        name={['delivery', 'deliveryMargin']}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (value === undefined || value === null || value === '') {
                                return Promise.resolve();
                              }
                              const num = parseFloat(value);
                              if (isNaN(num)) {
                                return Promise.reject(new Error('Delivery Margin must be a valid number.'));
                              }
                              if (num < 0) {
                                return Promise.reject(new Error('Delivery Margin must be a positive number.'));
                              }
                              if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                                return Promise.reject(new Error('Delivery Margin can have up to 2 decimal points.'));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber<number>
                          disabled={isFinalStatus}
                          controls={false}
                          size={'large'}
                          onBlur={onFinish}
                          onBeforeInput={(event: any) => preventNonNumericValue(event)}
                          style={{width: '100%'}}
                          suffix={<Text type={'secondary'}>%</Text>}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col>
                  <Form.Item label={<span style={{display: 'none'}}></span>}>
                    <Text className={'font-weight-600 font-size-16 no-wrap'}>
                      = {deliveryDetails.subTotalDeliveryCost}€
                    </Text>
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    label="Additional Services"
                    name={['delivery', 'additionalServices']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === '') {
                            return Promise.resolve();
                          }
                          const num = parseFloat(value);
                          if (isNaN(num)) {
                            return Promise.reject(new Error('Additional Services must be a valid number.'));
                          }
                          if (num < 0) {
                            return Promise.reject(new Error('Additional Services must be a positive number.'));
                          }
                          if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                            return Promise.reject(new Error('Additional Services can have up to 2 decimal points.'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber<number>
                      disabled={isFinalStatus}
                      controls={false}
                      size={'large'}
                      onBlur={onFinish}
                      onBeforeInput={(event: any) => preventNonNumericValue(event)}
                      style={{width: '100%'}}
                      suffix={<Text type={'secondary'}>€</Text>}
                    />
                  </Form.Item>
                </Col>
                {isDDP && (
                  <>
                    <Col span={6}>
                      <Form.Item
                        label="Local Taxes"
                        name={['delivery', 'localTaxes']}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (value === undefined || value === null || value === '') {
                                return Promise.resolve();
                              }
                              const num = parseFloat(value);
                              if (isNaN(num)) {
                                return Promise.reject(new Error('Local Taxes must be a valid number.'));
                              }
                              if (num < 0) {
                                return Promise.reject(new Error('Local Taxes must be a positive number.'));
                              }
                              if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                                return Promise.reject(new Error('Local Taxes can have up to 2 decimal points.'));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber<number>
                          disabled={isFinalStatus}
                          controls={false}
                          size={'large'}
                          onBlur={onFinish}
                          onBeforeInput={(event: any) => preventNonNumericValue(event)}
                          style={{width: '100%'}}
                          suffix={<Text type={'secondary'}>€</Text>}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label="Import Fees"
                        name={['delivery', 'importFees']}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (value === undefined || value === null || value === '') {
                                return Promise.resolve();
                              }
                              const num = parseFloat(value);
                              if (isNaN(num)) {
                                return Promise.reject(new Error('Import Fees must be a valid number.'));
                              }
                              if (num < 0) {
                                return Promise.reject(new Error('Import Fees must be a positive number.'));
                              }
                              if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                                return Promise.reject(new Error('Import Fees can have up to 2 decimal points.'));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber<number>
                          disabled={isFinalStatus}
                          controls={false}
                          size={'large'}
                          onBlur={onFinish}
                          onBeforeInput={(event: any) => preventNonNumericValue(event)}
                          style={{width: '100%'}}
                          suffix={<Text type={'secondary'}>€</Text>}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}
              </>
            )}
          </Row>
        </Col>
      </Row>
      <CustomAddressAddEdit
        modalState={customAddressState}
        onModalStateChange={onCustomAddressStateChange}
        dataToEdit={dataToEdit}
        onSuccess={async () => {
          form.setFieldValue('customShippingAddressId', 'default');
          onFinish();
        }}
      />
    </>
  );
});
